<div [id]="id" tabindex="-1" class="fixed top-0 left-0 right-0 z-50 hidden w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full">
    <div class="relative w-150 max-h-full rounded-lg">
        <div class="relative primary-purple-light-2-background rounded-lg shadow dark:bg-gray-700 p-6.25">
            <!-- Header -->
            <div class="flex items-center gap-2">
                <h3 class="text-xl font-bold primary-purple-text dark:text-gray-400 flex-1">
                    {{ getModalTitle() }}
                </h3>
                @if (showHeaderActions() && !pendingDeactivate) {
                    @if (showHeaderEdit()) {
                        <div class="primary-purple-border px-2 py-1 flex items-center border-solid border rounded text-sm gap-1 cursor-pointer" (click)="onHeaderEdit()">
                            <img [src]="images.services.edit" alt="edit" class="w-4.5 h-4.5" />
                            <label class="cursor-pointer">{{ literalService.get('edit', true) }}</label>
                        </div>
                    }
                    @if (userRole === UserRole.SUPERADMIN) {
                        @if (isCurrentItemActive()) {
                            <div class="primary-purple-border px-2 py-1 flex items-center border-solid border rounded text-sm gap-1 cursor-pointer" (click)="onHeaderDeactivate()">
                                <label class="cursor-pointer">{{ literalService.get('deactivate', true) }}</label>
                            </div>
                        } @else {
                            <div class="primary-purple-background text-white px-2 py-1 flex items-center rounded text-sm gap-1 cursor-pointer" (click)="onHeaderActivate()">
                                <label class="cursor-pointer text-white">{{ literalService.get('activate', true) }}</label>
                            </div>
                        }
                    }
                }
                <button (click)="modal.hide()" type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
                    <img [src]="images.close" class="h-6 w-6" />
                </button>
            </div>

            <!-- Tabs -->
            <div class="modal-fare-tabs flex gap-1 mt-6 mb-6 -mx-6.25 px-6.25 neutrals-grey-02-border" style="border-bottom-width: 1px;">
                <button
                    type="button"
                    class="modal-fare-tab"
                    [ngClass]="{ 'active': activeTab === 'fares' }"
                    (click)="setActiveTab('fares')"
                >
                    {{ literalService.get('services.routes.tabs.fares', true) }}
                </button>
                <button
                    type="button"
                    class="modal-fare-tab"
                    [ngClass]="{ 'active': activeTab === 'promotionalCodes' }"
                    (click)="setActiveTab('promotionalCodes')"
                >
                    {{ literalService.get('services.routes.tabs.promotionalCodes', true) }}
                </button>
                <button
                    type="button"
                    class="modal-fare-tab"
                    [ngClass]="{ 'active': activeTab === 'temporalPasses' }"
                    (click)="setActiveTab('temporalPasses')"
                >
                    {{ literalService.get('services.routes.tabs.temporalPasses', true) }}
                </button>
            </div>

            <!-- Fares Tab -->
            @if (activeTab === 'fares') {
                <form #form novalidate (ngSubmit)="onSubmit()">
                    @if (editingFare.id && editingFare.active === false) {
                        <div class="secondary-orange-light-background secondary-orange-02-text rounded px-3 py-2 mb-3 text-sm font-semibold">
                            {{ literalService.get('services.routes.deactivatedBanner', true) }}
                        </div>
                    }
                    <!-- Row 1: Fare Selector or Name input -->
                    <div class="flex flex-col">
                        @if (!isCreating) { 
                            <div class="flex items-center mb-1.5">
                                <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.selectFare', true) }}</label>
                            </div>
                            <select2
                                class="block w-full"
                                infiniteScroll
                                customSearchEnabled
                                [placeholder]="literalService.get('services.routes.fare.selectPlaceholder', true)"
                                [data]="faresValues"
                                [value]="editingFare.id"
                                [templates]="{option: fareOption}"
                                (update)="selectFare($event)"
                                (scroll)="loadMoreFare($event)"
                                (search)="searchFare($event)"
                            >
                                <ng-template #fareOption let-label="label">
                                    <app-select2-option [label]="label" />
                                </ng-template>
                            </select2>
                        } @else {
                            <div class="flex items-center mb-[6px]">
                                <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.name', true) }}</label>
                            </div>
                            <input
                                type="text"
                                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                [value]="editingFare.name || ''"
                                (change)="updateName($event)"
                                [placeholder]="literalService.get('services.routes.fare.namePlaceholder', true)"
                            />
                        }
                    </div>

                    <!-- Name rename (only in editing mode) -->
                    @if (isEditing) {
                        <div class="flex flex-col mt-4">
                            <div class="flex items-center mb-1.5">
                                <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.name', true) }}</label>
                            </div>
                            <input
                                type="text"
                                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                [value]="editingFare.name || ''"
                                (change)="updateName($event)"
                                [placeholder]="literalService.get('services.routes.fare.namePlaceholder', true)"
                            />
                        </div>
                    }

                    <!-- Fare Type (only when fare selected or creating) -->
                    @if (editingFare.id || isCreating) {
                        <div class="flex flex-col mt-4">
                            <div class="flex items-center mb-1.5">
                                <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.type', true) }}</label>
                            </div>
                            @if (isCreating || isEditing) {
                                <select2
                                    class="block w-full"
                                    [placeholder]="literalService.get('services.routes.fare.selectTypePlaceholder', true)"
                                    [data]="getFareTypes()"
                                    [value]="editingFare.type"
                                    [templates]="{option: fareTypeOption}"
                                    (update)="changeFareType($event)"
                                >
                                    <ng-template #fareTypeOption let-label="label">
                                        <app-select2-option [label]="label" />
                                    </ng-template>
                                </select2>
                            } @else {
                                <input
                                    type="text"
                                    class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                    [value]="literalService.get('fareType.' + editingFare.type, true)"
                                    disabled
                                />
                            }
                        </div>
                    }

                    <!-- Row: Base Price (only visible when fare selected or creating) -->
                    @if (editingFare.id || isCreating) {
                        <div class="flex gap-4 mt-4">
                            <div class="flex flex-col flex-1">
                                <div class="flex items-center mb-1.5">
                                    <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.basePrice', true) }}</label>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                    [value]="formatPrice(editingFare.basePrice)"
                                    [disabled]="!isCreating && !isEditing"
                                    (change)="updateBasePrice($event)"
                                    [placeholder]="literalService.get('services.routes.fare.basePricePlaceholder', true)"
                                />
                            </div>
                        </div>
                    }

                    <!-- Price Per Km (only for Flexible) -->
                    @if (editingFare.type === FareType.Flexible && (isCreating || isEditing || editingFare.id)) {
                        <div class="flex flex-col mt-4">
                            <div class="flex items-center mb-1.5">
                                <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.pricePerKm', true) }}</label>
                            </div>
                            <input
                                type="number"
                                step="0.01"
                                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                [value]="formatPrice(editingFare.variablePrice)"
                                [disabled]="!isCreating && !isEditing"
                                (change)="updateVariablePrice($event)"
                                [placeholder]="literalService.get('services.routes.fare.pricePerKmPlaceholder', true)"
                            />
                        </div>
                    }

                    <!-- Ranges (only for Ranged_Flexible) -->
                    @if (editingFare.type === FareType.Ranged_Flexible && (isCreating || isEditing || editingFare.id)) {
                        <div class="flex flex-col mt-4">
                            <div class="flex items-center justify-between mb-1.5">
                                <div class="flex items-center">
                                    <label class="font-normal text-base primary-purple-text">{{ literalService.get('services.routes.fare.ranges', true) }}</label>
                                </div>
                                @if (isCreating || isEditing) {
                                    <button type="button" class="flex items-center text-sm primary-purple-text cursor-pointer" (click)="addRange()">
                                        <img [src]="images.plus" alt="add" class="h-4 w-4 mr-1" />
                                        {{ literalService.get('services.routes.fare.addRange', true) }}
                                    </button>
                                }
                            </div>

                            <div class="max-h-50 overflow-y-auto">
                                @for (range of editingFare.priceRanges; track $index) {
                                    <div class="flex items-center gap-2 mb-2 p-2 bg-white rounded border">
                                        <div class="flex flex-col flex-1">
                                            <label class="text-xs text-gray-500 mb-1">{{ literalService.get('services.routes.fare.rangePrice', true) }}</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                class="w-full px-2 py-1 border rounded text-sm"
                                                [value]="range.price"
                                                [disabled]="!isCreating && !isEditing"
                                                (change)="updateRangePrice($index, $event)"
                                            />
                                        </div>
                                        <div class="flex flex-col flex-1">
                                            <label class="text-xs text-gray-500 mb-1">{{ literalService.get('services.routes.fare.rangeStart', true) }}</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                class="w-full px-2 py-1 border rounded text-sm"
                                                [value]="range.start"
                                                [disabled]="!isCreating && !isEditing"
                                                (change)="updateRangeStart($index, $event)"
                                            />
                                        </div>
                                        <div class="flex flex-col flex-1">
                                            <label class="text-xs text-gray-500 mb-1">{{ literalService.get('services.routes.fare.rangeEnd', true) }}</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                class="w-full px-2 py-1 border rounded text-sm"
                                                [value]="range.end"
                                                [disabled]="!isCreating && !isEditing"
                                                (change)="updateRangeEnd($index, $event)"
                                            />
                                        </div>
                                        @if (isCreating || isEditing) {
                                            <button type="button" class="self-end mb-1" (click)="removeRange($index)">
                                                <img [src]="images.services.delete" alt="delete" class="h-5 w-5 cursor-pointer" />
                                            </button>
                                        }
                                    </div>
                                }
                            </div>

                            @if ((!editingFare.priceRanges || editingFare.priceRanges.length === 0) && (isCreating || isEditing)) {
                                <div class="text-sm text-gray-500 text-center py-2">
                                    {{ literalService.get('services.routes.fare.noRanges', true) }}
                                </div>
                            }
                        </div>
                    }

                    <!-- Footer Buttons -->
                    @if (!pendingDeactivate) {
                        <div class="flex items-center mt-8" [ngClass]="{'justify-center': isCreating || isEditing, 'justify-end': !isCreating && !isEditing}">
                            @if (isCreating || isEditing) {
                                <button type="button" class="secondary mr-5 gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer" (click)="cancelEdit()">
                                    {{ literalService.get('cancel', true) }}
                                </button>
                                <button type="submit" class="primary gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer text-white" [disabled]="!isFormValid()">
                                    {{ literalService.get('save', true) }}
                                </button>
                            } @else {
                                <button type="button" class="primary gap-4 px-6 py-2 border-2 font-semibold uppercase cursor-pointer text-white" (click)="startCreating()">
                                    {{ literalService.get('services.routes.fare.newFare', true) }}
                                </button>
                            }
                        </div>
                    }
                </form>
            }

            <!-- Promotional Codes Tab -->
            <div [hidden]="activeTab !== 'promotionalCodes'">
                <app-promotional-codes-tab
                    [promotionalCodes]="promotionalCodes"
                    [selectedPromotionalCode]="selectedPromotionalCode"
                    [clientId]="clientId"
                    [userRole]="userRole"
                    [hideFooter]="pendingDeactivate"
                    (action)="promotionalCodeAction.emit($event)"
                    (deleteEvent)="deletePromotionalCodeEvent.emit($event)"
                />
            </div>

            <!-- Temporal Passes Tab -->
            <div [hidden]="activeTab !== 'temporalPasses'">
                <app-temporal-passes-tab
                    [temporalPasses]="temporalPasses"
                    [selectedTemporalPass]="selectedTemporalPass"
                    [fares]="fares"
                    [clientId]="clientId"
                    [userRole]="userRole"
                    [hideFooter]="pendingDeactivate"
                    (action)="temporalPassAction.emit($event)"
                    (deleteEvent)="deleteTemporalPassEvent.emit($event)"
                />
            </div>

            <!-- Deactivate inline confirmation panel -->
            @if (pendingDeactivate) {
                <div class="mt-8 pt-4 border-t neutrals-grey-02-border">
                    <p class="text-base primary-purple-text font-semibold mb-4">
                        {{ literalService.get(getDeactivateTextKey(), true) }}
                    </p>
                    <div class="flex items-center justify-center">
                        <button type="button" class="secondary mr-5 gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer" (click)="cancelPendingDeactivate()">
                            {{ literalService.get('cancel', true) }}
                        </button>
                        <button type="button" class="primary gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer text-white" (click)="confirmPendingDeactivate()">
                            {{ literalService.get('deactivate', true) }}
                        </button>
                    </div>
                </div>
            }
        </div>
    </div>
</div>
